<template>
  <div class="order-create">
    <div class="order-create__panel order-create__panel--header">
      <div class="order-create__toolbar">
        <h1 class="order-create__title">{{ pageTitle }}</h1>
        <button
          type="button"
          class="order-create__btn order-create__btn--secondary"
          @click="$router.push({ name: 'orders' })"
        >
          К списку заказов
        </button>
      </div>
    </div>

    <div class="order-create__panel order-create__panel--body">
      <p v-if="loading" class="order-create__status">
        {{ loadStatusText }}
      </p>

      <fieldset class="order-create__fieldset" :disabled="loading">
        <form @submit.prevent="submit">
          <div v-if="isEditMode" class="order-create__meta">
            <span class="order-create__meta-label">Обработал заказ:</span>
            <span class="order-create__meta-value">{{
              processedByName || "—"
            }}</span>
          </div>
          <h3 class="order-create__h3">Покупатель</h3>
          <div class="order-create__row">
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="customer_name"
                >Имя *</label
              >
              <input
                id="customer_name"
                v-model="form.customer_name"
                class="order-create__input"
                type="text"
                required
                autocomplete="name"
              />
            </div>
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="customer_phone"
                >Телефон *</label
              >
              <input
                id="customer_phone"
                v-model="form.customer_phone"
                v-mask="'+7 (###) ###-##-##'"
                :class="[
                  'order-create__input',
                  { 'order-create__input--error': fieldErrors.customer_phone },
                ]"
                type="text"
                inputmode="tel"
                autocomplete="tel"
                placeholder="+7 (999) 123-45-67"
                @input="clearPhoneError('customer_phone')"
              />
              <p
                v-if="fieldErrors.customer_phone"
                class="order-create__field-error"
                role="alert"
              >
                {{ fieldErrors.customer_phone }}
              </p>
            </div>
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="recipient_name"
                >Имя получателя</label
              >
              <input
                id="recipient_name"
                v-model="form.recipient_name"
                class="order-create__input"
                type="text"
              />
            </div>
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="recipient_phone"
                >Телефон получателя</label
              >
              <input
                id="recipient_phone"
                v-model="form.recipient_phone"
                v-mask="'+7 (###) ###-##-##'"
                :class="[
                  'order-create__input',
                  { 'order-create__input--error': fieldErrors.recipient_phone },
                ]"
                type="text"
                inputmode="tel"
                autocomplete="tel"
                placeholder="+7 (999) 123-45-67"
                @input="clearPhoneError('recipient_phone')"
              />
              <p
                v-if="fieldErrors.recipient_phone"
                class="order-create__field-error"
                role="alert"
              >
                {{ fieldErrors.recipient_phone }}
              </p>
            </div>
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="user_id"
                >ID пользователя (ЛК), необязательно</label
              >
              <input
                id="user_id"
                v-model.number="form.user_id"
                class="order-create__input"
                type="number"
                min="1"
              />
            </div>
          </div>

          <OrderProductsEditor v-model="form.products" />

          <h3 class="order-create__h3">Оплата и получение</h3>
          <div class="order-create__row">
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="delivery_type"
                >Способ получения *</label
              >
              <select
                id="delivery_type"
                v-model="form.delivery_type"
                class="order-create__select"
                required
              >
                <option
                  v-for="opt in deliveryTypeOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="payment_method"
                >Способ оплаты *</label
              >
              <select
                id="payment_method"
                v-model="form.payment_method"
                class="order-create__select"
                required
              >
                <option
                  v-for="opt in paymentMethodSelectOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="amount_delivery">
                Стоимость доставки, ₽
              </label>
              <input
                id="amount_delivery"
                v-model="form.amount_delivery"
                class="order-create__input"
                type="number"
                min="0"
                step="0.01"
                placeholder="Оставьте пустым для авторасчета"
                @input="onDeliveryAmountInput"
              />
            </div>
          </div>

          <template v-if="form.delivery_type === 'delivery'">
            <div class="order-create__row">
              <div class="order-create__col order-create__col--third">
                <label class="order-create__label" for="delivery_date"
                  >Дата доставки *</label
                >
                <input
                  id="delivery_date"
                  v-model="form.delivery_date"
                  class="order-create__input"
                  type="date"
                  required
                />
              </div>
              <div class="order-create__col order-create__col--third">
                <label class="order-create__label" for="delivery_type_id"
                  >Тип доставки *</label
                >
                <select
                  id="delivery_type_id"
                  v-model="form.delivery_type_id"
                  class="order-create__select"
                  required
                >
                  <option disabled value="">Выберите тип</option>
                  <option
                    v-for="t in deliveryTypesList"
                    :key="t.id"
                    :value="t.id"
                  >
                    {{ t.title }}
                  </option>
                </select>
              </div>
              <div
                v-if="needsDeliverySlot"
                class="order-create__col order-create__col--third"
              >
                <label class="order-create__label" for="delivery_slot_id"
                  >Интервал *</label
                >
                <select
                  id="delivery_slot_id"
                  v-model="form.delivery_slot_id"
                  class="order-create__select"
                  required
                >
                  <option disabled value="">Выберите интервал</option>
                  <option
                    v-for="s in slotsForSelectedType"
                    :key="s.id"
                    :value="s.id"
                  >
                    {{ s.label }}
                  </option>
                </select>
              </div>
              <div
                v-if="showDeliveryModeOptions"
                class="order-create__col order-create__col--full"
              >
                <label class="order-create__label order-create__checkbox-label">
                  <input
                    :checked="form.delivery_mode === 'exact'"
                    :disabled="!exactModeAvailable"
                    class="order-create__checkbox"
                    type="checkbox"
                    @change="onExactModeToggle($event.target.checked)"
                  />
                  {{ exactModeLabel }}
                </label>
                <label class="order-create__label order-create__checkbox-label">
                  <input
                    :checked="form.delivery_mode === 'express'"
                    :disabled="!expressAvailable"
                    class="order-create__checkbox"
                    type="checkbox"
                    @change="onExpressModeToggle($event.target.checked)"
                  />
                  {{ expressModeLabel }}
                </label>
                <p
                  v-if="form.delivery_mode === 'express' && expressTimeLabel"
                  class="order-create__hint"
                >
                  Ближайший интервал: {{ expressTimeLabel }}
                </p>
              </div>
              <div
                v-if="form.delivery_mode === 'exact' && showDeliveryModeOptions"
                class="order-create__col order-create__col--third"
              >
                <label class="order-create__label" for="exact_time"
                  >Точное время *</label
                >
                <input
                  id="exact_time"
                  v-model="form.exact_time"
                  :max="exactTimeMax"
                  :min="exactTimeMin"
                  :step="exactTimeStepSeconds"
                  class="order-create__input"
                  required
                  type="time"
                />
              </div>
              <div class="order-create__col order-create__col--full">
                <label class="order-create__label" for="delivery_address"
                  >Адрес доставки *</label
                >
                <input
                  id="delivery_address"
                  v-model="form.delivery_address"
                  class="order-create__input"
                  type="text"
                  required
                />
              </div>
              <div class="order-create__col order-create__col--full">
                <label class="order-create__label order-create__checkbox-label">
                  <input
                    v-model="form.photo_required"
                    class="order-create__checkbox"
                    type="checkbox"
                  />
                  Фото при доставке
                </label>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="order-create__row">
              <div class="order-create__col order-create__col--third">
                <label class="order-create__label" for="pickup_date"
                  >Дата самовывоза *</label
                >
                <input
                  id="pickup_date"
                  v-model="form.delivery_date"
                  class="order-create__input"
                  type="date"
                  required
                />
              </div>
              <div class="order-create__col order-create__col--two-thirds">
                <label class="order-create__label" for="pickup_address_id"
                  >Точка самовывоза *</label
                >
                <select
                  id="pickup_address_id"
                  v-model="form.pickup_address_id"
                  class="order-create__select"
                  required
                >
                  <option disabled value="">Выберите точку</option>
                  <option
                    v-for="loc in locationsList"
                    :key="loc.id"
                    :value="loc.id"
                  >
                    {{ loc.address }}
                  </option>
                </select>
              </div>
            </div>
          </template>

          <h3 class="order-create__h3 order-create__h3--spaced">Комментарий</h3>
          <div class="order-create__row">
            <div class="order-create__col order-create__col--full">
              <label class="order-create__label" for="comment"
                >Комментарий к заказу</label
              >
              <textarea
                id="comment"
                v-model="form.comment"
                class="order-create__textarea"
                rows="3"
              />
            </div>
            <div class="order-create__col order-create__col--full">
              <label class="order-create__label" for="staff_comment"
                >Комментарий к заказу для персонала</label
              >
              <textarea
                id="staff_comment"
                v-model="form.staff_comment"
                class="order-create__textarea"
                rows="3"
              />
            </div>
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="card_text"
                >Текст открытки</label
              >
              <textarea
                id="card_text"
                v-model="form.card_text"
                class="order-create__textarea"
                rows="3"
              />
            </div>
            <div class="order-create__col order-create__col--half">
              <label class="order-create__label" for="promocode"
                >Промокод</label
              >
              <input
                id="promocode"
                v-model="form.promocode"
                class="order-create__input"
                type="text"
              />
            </div>
            <div class="order-create__col order-create__col--full">
              <label class="order-create__label order-create__checkbox-label">
                <input
                  v-model="form.do_not_call"
                  class="order-create__checkbox"
                  type="checkbox"
                />
                Не звонить клиенту
              </label>
            </div>
          </div>

          <div class="order-create__submit-wrap">
            <button
              type="submit"
              class="order-create__btn order-create__btn--primary"
              :disabled="submitting || loading"
            >
              {{ submitButtonLabel }}
            </button>
          </div>
        </form>
      </fieldset>
    </div>
  </div>
</template>

<script>
import api from "@/api/axios";
import OrderProductsEditor from "@/components/orders/OrderProductsEditor.vue";
import { mapActions, mapState } from "vuex";

/** Как на бэкенде CreateOrderRequest: 7 + 10 цифр после нормализации */
function normalizeRussianMobile(input) {
  if (input == null || input === "") {
    return null;
  }
  const phone = String(input).replace(/\D/g, "");
  if (phone === "") {
    return null;
  }
  let p = phone;
  if (p.length === 10 && p[0] === "9") {
    p = "7" + p;
  }
  if (p.length === 11 && p[0] === "8") {
    p = "7" + p.slice(1);
  }
  return p.length === 11 && p[0] === "7" ? p : null;
}

/** Отображение в поле с v-mask (как в локациях) при загрузке заказа */
function formatRussianMobileMaskDisplay(input) {
  if (input == null || String(input).trim() === "") {
    return "";
  }
  const normalized = normalizeRussianMobile(input);
  if (normalized) {
    const rest = normalized.slice(1);
    return `+7 (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6, 8)}-${rest.slice(8, 10)}`;
  }
  return String(input).trim();
}

/** Текст для снек-бара: message + все строки из errors (422 и др.) */
function formatApiErrorForNotify(data) {
  if (!data || typeof data !== "object") {
    return "";
  }
  const seen = new Set();
  const parts = [];
  const push = (s) => {
    const t = String(s).trim();
    if (t && !seen.has(t)) {
      seen.add(t);
      parts.push(t);
    }
  };
  if (data.message) {
    push(data.message);
  }
  if (data.errors && typeof data.errors === "object") {
    Object.keys(data.errors).forEach((key) => {
      const val = data.errors[key];
      const msgs = Array.isArray(val) ? val : [val];
      msgs.forEach((m) => {
        if (m != null && m !== "") {
          push(m);
        }
      });
    });
  }
  return parts.join("\n");
}

function toOmskDateInputValue(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Omsk",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  if (!year || !month || !day) {
    return "";
  }
  return `${year}-${month}-${day}`;
}

function parseNonNegativeMoney(value) {
  if (value == null) {
    return null;
  }
  const raw = String(value).trim().replace(",", ".");
  if (raw === "") {
    return null;
  }
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  return amount;
}

export default {
  name: "OrderCreate",
  components: {
    OrderProductsEditor,
  },
  data() {
    return {
      loading: true,
      submitting: false,
      fieldErrors: {
        customer_phone: "",
        recipient_phone: "",
      },
      isHydratingOrderForm: false,
      deliveryAmountTouched: false,
      deliveryOptions: null,
      deliveryOptionsTimer: null,
      form: {
        customer_name: "",
        customer_phone: "",
        recipient_name: "",
        recipient_phone: "",
        user_id: null,
        payment_method: "online",
        delivery_type: "delivery",
        products: [
          {
            _lineId: 1,
            product_id: null,
            quantity: 1,
            unit_price: null,
            selectedProductName: "",
            product_slug: "",
            product_thumb_url: "",
            productSearch: "",
            productSearchResults: [],
          },
        ],
        delivery_date: "",
        delivery_type_id: "",
        delivery_slot_id: "",
        delivery_mode: "interval",
        exact_time: "",
        delivery_address: "",
        amount_delivery: "",
        photo_required: false,
        pickup_address_id: "",
        comment: "",
        staff_comment: null,
        card_text: "",
        promocode: "",
        do_not_call: false,
        processed_by_name: "",
      },
      deliveryTypeOptions: [
        { text: "Доставка", value: "delivery" },
        { text: "Самовывоз", value: "pickup" },
      ],
    };
  },
  computed: {
    ...mapState({
      locationsState: (state) => state.locations.locations,
      deliveryTypesListRaw: (state) => state.deliveryTypes.list,
      deliverySlotsList: (state) => state.deliverySlots.list,
    }),
    locationsList() {
      return Array.isArray(this.locationsState) ? this.locationsState : [];
    },
    deliveryTypesList() {
      const raw = Array.isArray(this.deliveryTypesListRaw)
        ? this.deliveryTypesListRaw
        : [];
      return raw.filter((t) => t.is_active !== false);
    },
    slotsForSelectedType() {
      const raw = this.form.delivery_type_id;
      const typeId = raw === "" || raw == null ? null : Number(raw);
      if (
        typeId == null ||
        Number.isNaN(typeId) ||
        !Array.isArray(this.deliverySlotsList)
      ) {
        return [];
      }
      return this.deliverySlotsList
        .filter(
          (s) => Number(s.delivery_type_id) === typeId && s.is_active !== false
        )
        .map((s) => ({
          id: s.id,
          label: `${s.time_from || ""}–${s.time_to || ""}`,
        }));
    },
    selectedDeliveryTypeItem() {
      const raw = this.form.delivery_type_id;
      const typeId = raw === "" || raw == null ? null : Number(raw);
      if (typeId == null || Number.isNaN(typeId)) {
        return null;
      }
      return (
        this.deliveryTypesList.find((t) => Number(t.id) === typeId) || null
      );
    },
    isDayDelivery() {
      return Boolean(
        this.selectedDeliveryTypeItem &&
          this.selectedDeliveryTypeItem.code === "day"
      );
    },
    showDeliveryModeOptions() {
      return this.form.delivery_type === "delivery" && this.isDayDelivery;
    },
    needsDeliverySlot() {
      if (this.form.delivery_type !== "delivery") {
        return false;
      }
      if (this.showDeliveryModeOptions && this.form.delivery_mode !== "interval") {
        return false;
      }
      return true;
    },
    productsSum() {
      return this.form.products.reduce((sum, row) => {
        if (row.product_id == null || row.product_id === "") {
          return sum;
        }
        const price = Number(row.unit_price);
        const qty = Number(row.quantity) || 1;
        if (!Number.isFinite(price) || price < 0) {
          return sum;
        }
        return sum + price * qty;
      }, 0);
    },
    dayDeliveryModes() {
      const items =
        this.deliveryOptions && Array.isArray(this.deliveryOptions.items)
          ? this.deliveryOptions.items
          : [];
      const day = items.find((item) => item && item.code === "day");
      return day && day.delivery_modes ? day.delivery_modes : null;
    },
    exactModePrice() {
      const price =
        this.dayDeliveryModes &&
        this.dayDeliveryModes.exact &&
        this.dayDeliveryModes.exact.price;
      return price != null ? Number(price) : 600;
    },
    expressModePrice() {
      const price =
        this.dayDeliveryModes &&
        this.dayDeliveryModes.express &&
        this.dayDeliveryModes.express.price;
      return price != null ? Number(price) : 650;
    },
    exactModeAvailable() {
      const exact =
        this.dayDeliveryModes && this.dayDeliveryModes.exact
          ? this.dayDeliveryModes.exact
          : null;
      return exact && Object.prototype.hasOwnProperty.call(exact, "available")
        ? Boolean(exact.available)
        : true;
    },
    expressAvailable() {
      const express =
        this.dayDeliveryModes && this.dayDeliveryModes.express
          ? this.dayDeliveryModes.express
          : null;
      return Boolean(express && express.available);
    },
    exactTimeMin() {
      return (
        (this.dayDeliveryModes &&
          this.dayDeliveryModes.exact &&
          this.dayDeliveryModes.exact.min_time) ||
        "09:00"
      );
    },
    exactTimeMax() {
      return (
        (this.dayDeliveryModes &&
          this.dayDeliveryModes.exact &&
          this.dayDeliveryModes.exact.max_time) ||
        "20:55"
      );
    },
    exactTimeStepSeconds() {
      const stepMinutes =
        this.dayDeliveryModes &&
        this.dayDeliveryModes.exact &&
        this.dayDeliveryModes.exact.step_minutes;
      const minutes = Number(stepMinutes);
      return (Number.isFinite(minutes) && minutes > 0 ? minutes : 5) * 60;
    },
    exactModeLabel() {
      if (!this.exactModeAvailable) {
        return "Доставка к точному времени недоступна на выбранную дату";
      }
      return `Доставка к точному времени (${this.exactModePrice.toLocaleString(
        "ru-RU"
      )} ₽)`;
    },
    expressModeLabel() {
      if (!this.expressAvailable) {
        return "Срочная доставка недоступна на выбранную дату";
      }
      return `Срочная доставка на ближайшее время (${this.expressModePrice.toLocaleString(
        "ru-RU"
      )} ₽)`;
    },
    expressTimeLabel() {
      const express =
        this.dayDeliveryModes && this.dayDeliveryModes.express
          ? this.dayDeliveryModes.express
          : null;
      if (!express || !express.available || !express.time_from || !express.time_to) {
        return "";
      }
      return `${express.time_from} – ${express.time_to}`;
    },
    editOrderId() {
      const raw = this.$route.params.id;
      if (raw == null || raw === "") {
        return null;
      }
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? null : n;
    },
    isEditMode() {
      return this.editOrderId != null;
    },
    pageTitle() {
      return this.isEditMode
        ? `Редактирование заказа №${this.editOrderId}`
        : "Новый заказ";
    },
    submitButtonLabel() {
      return this.isEditMode ? "Сохранить изменения" : "Создать заказ";
    },
    processedByName() {
      const value = this.form.processed_by_name;
      return value && String(value).trim() ? String(value).trim() : "";
    },
    loadStatusText() {
      return this.isEditMode ? "Загрузка заказа…" : "Загрузка справочников…";
    },
    paymentMethodSelectOptions() {
      if (this.form.delivery_type === "delivery") {
        return [{ text: "Онлайн", value: "online" }];
      }
      return [
        { text: "В магазине", value: "on_delivery" },
        { text: "Онлайн", value: "online" },
      ];
    },
  },
  watch: {
    "form.delivery_type"() {
      if (this.isHydratingOrderForm) {
        return;
      }
      if (
        this.form.delivery_type === "delivery" &&
        this.form.payment_method === "on_delivery"
      ) {
        this.form.payment_method = "online";
      }
      if (this.form.delivery_type !== "delivery") {
        this.form.delivery_mode = "interval";
        this.form.exact_time = "";
        this.deliveryOptions = null;
      } else {
        this.scheduleFetchDeliveryOptions();
      }
    },
    "form.delivery_type_id"() {
      if (this.isHydratingOrderForm) {
        return;
      }
      if (!this.isDayDelivery && this.form.delivery_mode !== "interval") {
        this.form.delivery_mode = "interval";
        this.form.exact_time = "";
      }
      const hasSelectedSlot =
        this.form.delivery_slot_id !== null &&
        this.form.delivery_slot_id !== undefined &&
        this.form.delivery_slot_id !== "";
      if (!hasSelectedSlot) {
        return;
      }
      const selectedSlotIsAvailable = this.slotsForSelectedType.some(
        (slot) => String(slot.id) === String(this.form.delivery_slot_id)
      );
      if (!selectedSlotIsAvailable) {
        this.form.delivery_slot_id = "";
      }
    },
    "form.delivery_date"() {
      if (this.isHydratingOrderForm) {
        return;
      }
      this.scheduleFetchDeliveryOptions();
    },
    productsSum() {
      if (this.isHydratingOrderForm) {
        return;
      }
      this.scheduleFetchDeliveryOptions();
    },
    expressAvailable(available) {
      if (this.isHydratingOrderForm) {
        return;
      }
      if (!available && this.form.delivery_mode === "express") {
        this.form.delivery_mode = "interval";
      }
    },
    exactModeAvailable(available) {
      if (this.isHydratingOrderForm) {
        return;
      }
      if (!available && this.form.delivery_mode === "exact") {
        this.form.delivery_mode = "interval";
        this.form.exact_time = "";
      }
    },
  },
  async created() {
    try {
      await Promise.all([
        this.getLocations(),
        this.fetchDeliveryTypes(),
        this.fetchDeliverySlots(),
      ]);
      if (this.isEditMode) {
        await this.loadOrderForEdit();
      }
      this.scheduleFetchDeliveryOptions();
    } catch (e) {
      this.$notify?.({
        type: "danger",
        title: "Ошибка загрузки",
        message: e.message || "Не удалось загрузить справочники",
      });
      if (this.isEditMode) {
        this.$router.push({ name: "orders" });
      }
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    if (this.deliveryOptionsTimer) {
      clearTimeout(this.deliveryOptionsTimer);
    }
  },
  methods: {
    ...mapActions({
      getLocations: "locations/getLocations",
      fetchDeliveryTypes: "deliveryTypes/fetchList",
      fetchDeliverySlots: "deliverySlots/fetchList",
    }),
    formatHhMm(value) {
      if (value == null || value === "") {
        return "";
      }
      const match = String(value).match(/(\d{2}):(\d{2})/);
      return match ? `${match[1]}:${match[2]}` : "";
    },
    onExactModeToggle(checked) {
      if (checked) {
        if (!this.exactModeAvailable) {
          return;
        }
        this.form.delivery_mode = "exact";
        this.form.delivery_slot_id = "";
        return;
      }
      if (this.form.delivery_mode === "exact") {
        this.form.delivery_mode = "interval";
        this.form.exact_time = "";
      }
    },
    onExpressModeToggle(checked) {
      if (checked) {
        if (!this.expressAvailable) {
          return;
        }
        this.form.delivery_mode = "express";
        this.form.delivery_slot_id = "";
        this.form.exact_time = "";
        return;
      }
      if (this.form.delivery_mode === "express") {
        this.form.delivery_mode = "interval";
      }
    },
    scheduleFetchDeliveryOptions() {
      if (this.deliveryOptionsTimer) {
        clearTimeout(this.deliveryOptionsTimer);
      }
      this.deliveryOptionsTimer = setTimeout(() => {
        this.fetchDeliveryOptions();
      }, 250);
    },
    async fetchDeliveryOptions() {
      if (
        this.form.delivery_type !== "delivery" ||
        !this.form.delivery_date
      ) {
        this.deliveryOptions = null;
        return;
      }
      try {
        const res = await api.get("/delivery/options", {
          params: {
            date: this.form.delivery_date,
            cart_sum: Math.round(this.productsSum),
          },
        });
        this.deliveryOptions =
          res.data && typeof res.data === "object" ? res.data : null;
      } catch (e) {
        this.deliveryOptions = null;
      }
    },
    formatOrderDateForInput(value) {
      if (value == null || value === "") {
        return "";
      }
      const s = (typeof value === "string" ? value : String(value)).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return s;
      }
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        return toOmskDateInputValue(d);
      }
      return "";
    },
    async loadOrderForEdit() {
      try {
        const res = await api.get(`/admin/orders/${this.editOrderId}`);
        const order = res.data && res.data.data;
        if (!order || typeof order !== "object") {
          throw new Error("Некорректный ответ сервера");
        }
        this.applyOrderToForm(order);
      } catch (e) {
        const data = e.response && e.response.data;
        const msg =
          formatApiErrorForNotify(data) ||
          (data && data.message) ||
          e.message ||
          "Ошибка";
        this.$notify?.({
          type: "danger",
          title: "Не удалось загрузить заказ",
          message: msg,
        });
        this.$router.push({ name: "orders" });
      }
    },
    applyOrderToForm(order) {
      this.isHydratingOrderForm = true;
      let lineCounter = 1;
      const mappedProducts =
        order.products &&
        Array.isArray(order.products) &&
        order.products.length > 0
          ? order.products.map((row) => {
              const pid =
                row.product && row.product.id != null
                  ? row.product.id
                  : row.product_id != null && row.product_id !== ""
                  ? row.product_id
                  : null;
              const name = (
                row.product && row.product.name ? String(row.product.name) : ""
              ).trim();
              const product = row.product;
              let product_thumb_url = "";
              let product_slug = "";
              if (product && typeof product === "object") {
                product_slug = product.slug ? String(product.slug) : "";
                product_thumb_url = (() => {
                  const photos = Array.isArray(product.photos)
                    ? product.photos
                    : [];
                  const ph = photos.length ? photos[0] : null;
                  return ph
                    ? ph.thumbnail_url || ph.featured_url || ph.url || ""
                    : "";
                })();
              }
              return {
                _lineId: lineCounter++,
                product_id: pid,
                quantity: row.qty != null ? Number(row.qty) : 1,
                unit_price:
                  row.unit_price != null && row.unit_price !== ""
                    ? Number(row.unit_price)
                    : null,
                selectedProductName: name,
                productSearch: name,
                product_slug,
                product_thumb_url,
                productSearchResults: [],
              };
            })
          : [
              {
                _lineId: lineCounter++,
                product_id: null,
                quantity: 1,
                unit_price: null,
                selectedProductName: "",
                product_slug: "",
                product_thumb_url: "",
                productSearch: "",
                productSearchResults: [],
              },
            ];

      const inferredDelivery =
        order.delivery_type === "pickup" || order.delivery_type === "delivery"
          ? order.delivery_type
          : order.full_address
          ? "delivery"
          : "pickup";

      this.form.customer_name = (
        order.customer_name ||
        order.name ||
        ""
      ).trim();
      this.form.customer_phone = formatRussianMobileMaskDisplay(
        order.customer_phone || order.phone || ""
      );
      this.form.recipient_name = order.recipient_name || "";
      this.form.recipient_phone = formatRussianMobileMaskDisplay(
        order.recipient_phone || ""
      );
      this.form.user_id =
        order.user_id != null && order.user_id !== ""
          ? Number(order.user_id)
          : null;
      this.form.payment_method = order.payment_method || "on_delivery";
      this.form.delivery_type = inferredDelivery;
      this.form.products = mappedProducts;
      this.form.amount_delivery =
        order.amount_delivery != null && order.amount_delivery !== ""
          ? String(order.amount_delivery)
          : "";
      this.deliveryAmountTouched = false;

      if (
        this.form.delivery_type === "delivery" &&
        this.form.payment_method === "on_delivery"
      ) {
        this.form.payment_method = "online";
      }

      if (inferredDelivery === "delivery") {
        this.form.delivery_date = this.formatOrderDateForInput(
          order.date_delivery
        );
        this.form.delivery_type_id =
          order.delivery_type_id != null ? String(order.delivery_type_id) : "";
        const mode = order.delivery_mode;
        this.form.delivery_mode =
          mode === "exact" || mode === "express" || mode === "interval"
            ? mode
            : "interval";
        this.form.exact_time =
          this.form.delivery_mode === "exact"
            ? this.formatHhMm(order.time_delivery_from)
            : "";
        this.form.delivery_slot_id =
          this.form.delivery_mode === "interval" && order.delivery_slot_id != null
            ? String(order.delivery_slot_id)
            : "";
        this.form.delivery_address = order.full_address || "";
      } else {
        this.form.delivery_date = this.formatOrderDateForInput(
          order.date_delivery
        );
        this.form.delivery_type_id = "";
        this.form.delivery_slot_id = "";
        this.form.delivery_mode = "interval";
        this.form.exact_time = "";
        this.form.delivery_address = "";
      }

      this.form.photo_required = Boolean(order.photo_required);
      this.form.pickup_address_id =
        order.pickup_address_id != null ? String(order.pickup_address_id) : "";
      this.form.comment = order.notes ? String(order.notes) : "";
      if (Object.prototype.hasOwnProperty.call(order, "staff_comment")) {
        this.form.staff_comment =
          order.staff_comment != null ? String(order.staff_comment) : "";
      } else {
        this.form.staff_comment = null;
      }
      this.form.card_text = order.card_text ? String(order.card_text) : "";
      this.form.promocode = order.promo_code ? String(order.promo_code) : "";
      this.form.do_not_call = Boolean(order.do_not_call);
      this.form.processed_by_name =
        order.processed_by && order.processed_by.name
          ? String(order.processed_by.name)
          : "";
      this.$nextTick(() => {
        this.isHydratingOrderForm = false;
      });
    },
    validateProducts() {
      const ok = this.form.products.some(
        (p) => p.product_id != null && p.product_id !== ""
      );
      if (!ok) {
        this.$notify?.({
          type: "danger",
          title: "Товары",
          message:
            "Укажите хотя бы один товар: введите название в поиске и выберите позицию из списка.",
        });
        return false;
      }
      return true;
    },
    clearPhoneError(field) {
      if (this.fieldErrors[field]) {
        this.$set(this.fieldErrors, field, "");
      }
    },
    onDeliveryAmountInput() {
      this.deliveryAmountTouched = true;
    },
    validatePhones() {
      this.fieldErrors.customer_phone = "";
      this.fieldErrors.recipient_phone = "";

      const customer = normalizeRussianMobile(this.form.customer_phone);
      if (!customer) {
        this.fieldErrors.customer_phone =
          "Укажите корректный российский мобильный номер: 10 цифр с 9, либо 11 с +7 или 8.";
        return false;
      }

      const rawRecipient = this.form.recipient_phone;
      if (rawRecipient != null && String(rawRecipient).trim() !== "") {
        const rec = normalizeRussianMobile(rawRecipient);
        if (!rec) {
          this.fieldErrors.recipient_phone =
            "Некорректный номер получателя — оставьте поле пустым или укажите номер в том же формате, что и у покупателя.";
          return false;
        }
      }

      return true;
    },
    buildPayload() {
      const products = this.form.products
        .filter((p) => p.product_id != null && p.product_id !== "")
        .map((p) => {
          const row = {
            product_id: Number(p.product_id),
            quantity: Number(p.quantity) || 1,
          };
          if (p.unit_price != null && p.unit_price !== "") {
            row.unit_price = Number(p.unit_price);
          }
          return row;
        });

      const customerPhone = normalizeRussianMobile(this.form.customer_phone);
      const payload = {
        customer_name: this.form.customer_name.trim(),
        customer_phone: customerPhone,
        payment_method: this.form.payment_method,
        delivery_type: this.form.delivery_type,
        products,
      };

      if (this.form.recipient_name && String(this.form.recipient_name).trim()) {
        payload.recipient_name = String(this.form.recipient_name).trim();
      }
      const rawRecipient = this.form.recipient_phone;
      if (rawRecipient != null && String(rawRecipient).trim() !== "") {
        const rec = normalizeRussianMobile(rawRecipient);
        if (rec) {
          payload.recipient_phone = rec;
        }
      }
      if (this.form.user_id != null && this.form.user_id !== "") {
        payload.user_id = Number(this.form.user_id);
      }
      if (this.form.comment && this.form.comment.trim()) {
        payload.comment = this.form.comment.trim();
      }
      payload.staff_comment =
        this.form.staff_comment && String(this.form.staff_comment).trim()
          ? String(this.form.staff_comment).trim()
          : "";
      if (this.form.card_text && this.form.card_text.trim()) {
        payload.card_text = this.form.card_text.trim();
      }
      if (this.form.promocode && this.form.promocode.trim()) {
        payload.promocode = this.form.promocode.trim();
      }
      if (this.form.do_not_call) {
        payload.do_not_call = true;
      }

      if (this.form.delivery_type === "delivery") {
        payload.delivery_date = this.form.delivery_date.trim();
        payload.delivery_address = this.form.delivery_address.trim();
        payload.delivery_type_id = parseInt(
          String(this.form.delivery_type_id),
          10
        );
        const deliveryMode = this.showDeliveryModeOptions
          ? this.form.delivery_mode
          : "interval";
        payload.delivery_mode = deliveryMode;
        if (deliveryMode === "interval") {
          payload.delivery_slot_id = parseInt(
            String(this.form.delivery_slot_id),
            10
          );
        }
        if (deliveryMode === "exact") {
          payload.exact_time = this.formatHhMm(this.form.exact_time);
        }
        if (this.form.photo_required) {
          payload.photo_required = true;
        }
        if (this.deliveryAmountTouched) {
          payload.amount_delivery = parseNonNegativeMoney(this.form.amount_delivery);
        }
      } else {
        payload.delivery_date = this.form.delivery_date.trim();
        payload.pickup_address_id = parseInt(
          String(this.form.pickup_address_id),
          10
        );
      }

      return payload;
    },
    async submit() {
      if (!this.validatePhones()) {
        const msg =
          this.fieldErrors.customer_phone || this.fieldErrors.recipient_phone;
        this.$notify?.({
          type: "danger",
          title: "Проверьте телефоны",
          message: msg,
        });
        return;
      }
      if (!this.validateProducts()) {
        return;
      }

      this.submitting = true;
      try {
        const payload = this.buildPayload();
        if (this.isEditMode) {
          await api.put(`/admin/orders/${this.editOrderId}`, payload);
          this.$notify?.({
            type: "success",
            title: "Заказ сохранён",
          });
        } else {
          await api.post("/admin/orders", payload);
          this.$notify?.({ type: "success", title: "Заказ создан" });
        }
        this.$router.push({ name: "orders" });
      } catch (e) {
        const data = e.response && e.response.data;
        const fromApi = formatApiErrorForNotify(data);
        const notifyText =
          fromApi || (data && data.message) || e.message || "Ошибка";

        if (data && data.errors) {
          const err = data.errors;
          if (err.customer_phone && err.customer_phone[0]) {
            this.$set(
              this.fieldErrors,
              "customer_phone",
              err.customer_phone[0]
            );
          }
          if (err.recipient_phone && err.recipient_phone[0]) {
            this.$set(
              this.fieldErrors,
              "recipient_phone",
              err.recipient_phone[0]
            );
          }
        }

        this.$notify?.({
          type: "danger",
          title: this.isEditMode
            ? "Не удалось сохранить заказ"
            : "Не удалось создать заказ",
          message: notifyText,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.order-create {
  max-width: 1440px;

  &__panel {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    margin-bottom: 1rem;
    padding: 1rem 1.25rem;

    &--header {
      margin-bottom: 1rem;
    }
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  &__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
  }

  &__status {
    margin: 0 0 0.75rem;
    color: #666;
  }

  &__fieldset {
    margin: 0;
    padding: 0;
    border: none;
    min-width: 0;

    &:disabled {
      opacity: 0.65;
    }
  }

  &__h3 {
    margin: 0 0 0.75rem;
    font-size: 1.1rem;
    font-weight: 600;

    &--spaced {
      margin-top: 1.25rem;
    }
  }

  &__h3-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin: 0 0 0.75rem;

    .order-create__h3 {
      margin: 0;
    }

    &--spaced {
      margin-top: 1.25rem;
    }
  }

  &__products-total {
    margin: 0;
    font-size: 0.95rem;
    color: #374151;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -0.5rem 0.5rem;

    &--align-end {
      align-items: flex-end;
    }

    &--products {
      align-items: flex-start;
    }
  }

  &__col {
    box-sizing: border-box;
    padding: 0 0.5rem;
    margin-bottom: 0.75rem;

    &--product-search {
      flex: 1 1 280px;
      max-width: 100%;
      min-width: 0;
    }

    &--qty {
      flex: 0 0 100px;
      max-width: 120px;
    }

    &--unit {
      flex: 1 1 140px;
      max-width: 190px;
    }

    &--line-subtotal {
      flex: 0 0 118px;
      max-width: 130px;
    }

    &--half {
      flex: 1 1 200px;
      max-width: 100%;
    }

    &--third {
      flex: 1 1 160px;
      max-width: 100%;
    }

    &--quarter {
      flex: 1 1 120px;
      max-width: 100%;
    }

    &--two-thirds {
      flex: 1 1 280px;
      max-width: 100%;
    }

    &--full {
      flex: 1 1 100%;
      width: 100%;
    }

    &--narrow {
      flex: 0 0 auto;
      display: flex;
      align-items: flex-end;
      padding-bottom: 0.25rem;
      margin-top: 26px;
    }
  }

  &__line-subtotal {
    box-sizing: border-box;
    margin: 0;
    min-height: 2.375rem;
    padding: 0.5rem 0.625rem;
    display: flex;
    align-items: center;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #111827;
    line-height: 1.25;
  }

  &__search-results {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    margin-top: 6px;
    padding: 4px;
    background: #fff;
  }

  &__search-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 8px 10px;
    margin: 0;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(37, 99, 235, 0.06);
    }
  }

  &__search-name {
    flex: 1;
    min-width: 0;
    padding-right: 8px;
  }

  &__search-id {
    flex-shrink: 0;
    font-size: 0.8125rem;
    color: #6b7280;
  }

  &__label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  &__checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    margin-bottom: 0.5rem;
  }

  &__hint {
    margin: 0.35rem 0 0;
    font-size: 0.8125rem;
    color: #6b7280;
  }

  &__input,
  &__select,
  &__textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 0.5rem 0.65rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
  }

  &__input {
    &--error {
      border-color: #dc2626;
      box-shadow: 0 0 0 1px rgba(220, 38, 38, 0.2);
    }
  }

  &__field-error {
    margin: 0.35rem 0 0;
    font-size: 0.8125rem;
    color: #b91c1c;
  }

  &__textarea {
    resize: vertical;
    min-height: 4rem;
  }

  &__checkbox {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    border-radius: 4px;
    border: 1px solid transparent;
    cursor: pointer;
    background: #f0f0f0;
    color: #222;

    &:disabled,
    &--disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--primary {
      background: #2563eb;
      border-color: #2563eb;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1d4ed8;
        border-color: #1d4ed8;
      }
    }

    &--secondary {
      background: #e5e7eb;
      border-color: #d1d5db;
      color: #111;

      &:hover {
        background: #d1d5db;
      }
    }

    &--danger {
      background: #dc2626;
      border-color: #dc2626;
      color: #fff;

      &:hover {
        background: #b91c1c;
      }
    }

    &--outline {
      background: transparent;
      border-color: #2563eb;
      color: #2563eb;

      &:hover {
        background: rgba(37, 99, 235, 0.08);
      }
    }

    &--mb {
      margin-bottom: 1rem;
    }
  }

  &__submit-wrap {
    display: flex;
    justify-content: center;
    margin-top: 1.25rem;
    padding-top: 0.5rem;
  }

  &__meta {
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
    color: #374151;
  }

  &__meta-label {
    margin-right: 0.35rem;
    font-weight: 600;
  }

  &__meta-value {
    font-weight: 500;
  }
}
</style>
